import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Divider,
  Flex,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons'
import { CervicalExamData, generalPhysicalExamField } from '@hb/shared'
import { useCallback, useRef, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { FormElement } from '../../../../components/forms/Input'
import { SectionLabel } from '../../SectionLabel'

// §2.6 spec: Bishop Score = dilation (0–3) + effacement (0–3) + station (0–3)
//             + consistency (0–2) + position (0–2). Total: 0–13.
const getBishopScore = (exam: Partial<CervicalExamData>): number | null => {
  const { dilation, effacement, station, consistency, position } = exam
  if (
    dilation == null &&
    effacement == null &&
    station == null &&
    consistency == null &&
    position == null
  )
    return null

  let score = 0
  if (typeof dilation === 'number') {
    if (dilation >= 5) score += 3
    else if (dilation >= 3) score += 2
    else if (dilation >= 1) score += 1
  }
  if (typeof effacement === 'number') {
    if (effacement >= 80) score += 3
    else if (effacement >= 60) score += 2
    else if (effacement >= 40) score += 1
  }
  if (typeof station === 'number') {
    if (station >= 1) score += 3
    else if (station >= -1) score += 2
    else if (station === -2) score += 1
  }
  if (consistency === 'soft') score += 2
  else if (consistency === 'medium') score += 1
  if (position === 'anterior') score += 2
  else if (position === 'mid') score += 1
  return score
}

const bishopLabel = (score: number): { label: string; color: string } => {
  if (score >= 9) return { label: 'Favorable', color: 'green' }
  if (score >= 6) return { label: 'Moderate', color: 'orange' }
  return { label: 'Unfavorable', color: 'red' }
}

const CervicalExamInput = () => {
  const form = useForm()
  const {
    input: { value: exam },
  } = useField<Partial<CervicalExamData>>('data.cervicalExam', {
    subscription: { value: true },
  })
  // const {
  //   input: { value: hasCervicalExam },
  // } = useField<boolean | undefined>('data.hasCervicalExam', {
  //   subscription: { value: true },
  // })

  const setField = useCallback(
    (key: keyof CervicalExamData, val: unknown) => {
      form.change('data.cervicalExam', { ...(exam ?? {}), [key]: val })
    },
    [form, exam],
  )

  const bishop = exam ? getBishopScore(exam) : null

  const membraneStatus = (exam as any)?.membraneStatus as CervicalExamData['membraneStatus']

  return (
    <Box
      w="100%"
      mt={2}
      pl={2}
      borderLeft="2px solid"
      borderColor="blue.200"
      _dark={{ borderColor: 'blue.700' }}>
      <Text fontSize="xs" fontWeight={600} color="blue.600" _dark={{ color: 'blue.300' }} mb={2}>
        Cervical Exam
      </Text>

      {/* Membrane status — required when cervical exam is open */}
      <Box mb={2}>
        <Text fontSize="xs" color="gray.500" mb={1}>
          Membrane Status *
        </Text>
        <Select
          size="sm"
          value={membraneStatus ?? ''}
          onChange={e => setField('membraneStatus', e.target.value || undefined)}
          borderRadius={6}
          bg="white"
          _dark={{ bg: 'gray.800' }}
          borderColor={!membraneStatus ? 'orange.300' : 'gray.200'}>
          <option value="">Select...</option>
          <option value="intact">Intact</option>
          <option value="ruptured">Ruptured</option>
          <option value="bulging">Bulging</option>
        </Select>
        {!membraneStatus && (
          <Text fontSize="2xs" color="orange.500" mt={0.5}>
            Membrane status is required when a cervical exam is documented.
          </Text>
        )}
      </Box>

      <Flex gap={3} flexWrap="wrap" mb={2}>
        <Box minW="80px">
          <Text fontSize="xs" color="gray.500" mb={1}>
            Dilation (cm)
          </Text>
          <NumberInput
            size="sm"
            min={0}
            max={10}
            precision={1}
            step={0.5}
            value={exam?.dilation ?? ''}
            onChange={(_, v) => setField('dilation', isNaN(v) ? undefined : v)}>
            <NumberInputField borderRadius={6} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>

        <Box minW="80px">
          <Text fontSize="xs" color="gray.500" mb={1}>
            Effacement (%)
          </Text>
          <NumberInput
            size="sm"
            min={0}
            max={100}
            step={10}
            value={exam?.effacement ?? ''}
            onChange={(_, v) => setField('effacement', isNaN(v) ? undefined : v)}>
            <NumberInputField borderRadius={6} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </Box>

        <Box minW="80px">
          <Text fontSize="xs" color="gray.500" mb={1}>
            Station
          </Text>
          <Select
            size="sm"
            value={exam?.station != null ? String(exam.station) : ''}
            onChange={e =>
              setField('station', e.target.value === '' ? undefined : Number(e.target.value))
            }
            borderRadius={6}
            bg="white"
            _dark={{ bg: 'gray.800' }}>
            <option value="">—</option>
            <option value="-3">-3</option>
            <option value="-2">-2</option>
            <option value="-1">-1</option>
            <option value="0">0</option>
            <option value="1">+1</option>
            <option value="2">+2</option>
            <option value="3">+3</option>
          </Select>
        </Box>
      </Flex>

      <Flex gap={3} flexWrap="wrap" mb={2}>
        <Box minW="100px">
          <Text fontSize="xs" color="gray.500" mb={1}>
            Consistency
          </Text>
          <Select
            size="sm"
            value={exam?.consistency ?? ''}
            onChange={e =>
              setField(
                'consistency',
                (e.target.value as CervicalExamData['consistency']) || undefined,
              )
            }
            borderRadius={6}
            bg="white"
            _dark={{ bg: 'gray.800' }}>
            <option value="">—</option>
            <option value="firm">Firm</option>
            <option value="medium">Medium</option>
            <option value="soft">Soft</option>
          </Select>
        </Box>

        <Box minW="100px">
          <Text fontSize="xs" color="gray.500" mb={1}>
            Position
          </Text>
          <Select
            size="sm"
            value={exam?.position ?? ''}
            onChange={e =>
              setField('position', (e.target.value as CervicalExamData['position']) || undefined)
            }
            borderRadius={6}
            bg="white"
            _dark={{ bg: 'gray.800' }}>
            <option value="">—</option>
            <option value="posterior">Posterior</option>
            <option value="mid">Mid</option>
            <option value="anterior">Anterior</option>
          </Select>
        </Box>
      </Flex>

      {bishop != null && (
        <Flex align="center" gap={2} mt={1}>
          <Text fontSize="xs" fontWeight={600} color="gray.600">
            Bishop Score:
          </Text>
          <Badge colorScheme={bishopLabel(bishop).color} fontSize="sm" px={2}>
            {bishop} — {bishopLabel(bishop).label}
          </Badge>
        </Flex>
      )}

      <RoundedButton
        size="xs"
        variant="ghost"
        colorScheme="red"
        mt={2}
        onClick={() => {
          form.change('data.hasCervicalExam', false)
          form.change('data.cervicalExam', undefined)
        }}>
        Remove Cervical Exam
      </RoundedButton>
    </Box>
  )
}

const PHYSICAL_EXAM_DEFAULTS = {
  generalAppearance: { notes: 'Well-appearing' },
  heart: { notes: 'RRR, no murmurs' },
  lungs: { notes: 'Clear bilaterally' },
  abdomen: { notes: 'Soft, non-tender, fundal height as above' },
}

export const PhysicalExamInput = () => {
  const form = useForm()
  const defaultsApplied = useRef(false)

  const {
    input: { value: hasPhysicalExam },
  } = useField<boolean | undefined>('data.hasPhysicalExam', { subscription: { value: true } })
  const {
    input: { value: hasCervicalExam },
  } = useField<boolean | undefined>('data.hasCervicalExam', { subscription: { value: true } })
  const {
    input: { value: modality },
  } = useField<string | undefined>('data.visitLocation.modality', { subscription: { value: true } })

  const isTelehealth = modality === 'telehealth-video' || modality === 'telehealth-audio-only'

  const [isOpen, setIsOpen] = useState(!!hasPhysicalExam)

  const handleOpen = useCallback(() => {
    form.change('data.hasPhysicalExam', true)
    if (!defaultsApplied.current) {
      defaultsApplied.current = true
      const current = (form.getState().values?.data?.physical ?? {}) as Record<string, unknown>
      const defaults = PHYSICAL_EXAM_DEFAULTS as Record<string, unknown>
      // Only fill keys that are genuinely absent (undefined/null)
      const patched: Record<string, unknown> = { ...current }
      for (const [k, v] of Object.entries(defaults)) {
        if (!current[k]) patched[k] = v
      }
      form.change('data.physical', patched)
    }
    setIsOpen(true)
  }, [form])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
    } else {
      handleOpen()
    }
  }, [isOpen, handleOpen])

  const handleClear = useCallback(() => {
    form.change('data.hasPhysicalExam', false)
    form.change('data.physical', undefined)
    form.change('data.hasCervicalExam', false)
    form.change('data.cervicalExam', undefined)
    defaultsApplied.current = false
    setIsOpen(false)
  }, [form])

  return (
    <Box data-child-name="data.physical" w="100%" px={4} py={3}>
      {/* Always-visible card header */}
      <Flex
        align="center"
        justify="space-between"
        px={3}
        py={2}
        borderRadius={isOpen ? '6px 6px 0 0' : 6}
        border="1px solid"
        borderColor={hasPhysicalExam ? 'green.300' : 'gray.200'}
        bg={hasPhysicalExam ? 'green.50' : 'gray.50'}
        _dark={{
          bg: hasPhysicalExam ? 'green.900' : 'gray.800',
          borderColor: hasPhysicalExam ? 'green.600' : 'gray.600',
        }}
        cursor="pointer"
        onClick={handleToggle}>
        <Flex align="center" gap={2}>
          <SectionLabel>Physical Exam</SectionLabel>
          {hasPhysicalExam && (
            <Badge colorScheme="green" fontSize="2xs">
              Documented
            </Badge>
          )}
          {!hasPhysicalExam && !isTelehealth && (
            <Text fontSize="xs" color="gray.400" fontStyle="italic">
              Tap to expand and document
            </Text>
          )}
          {!hasPhysicalExam && isTelehealth && (
            <Text fontSize="xs" color="orange.500" fontStyle="italic">
              Physical exam not performable via telehealth
            </Text>
          )}
        </Flex>
        {isOpen ? <ChevronUpIcon color="gray.400" /> : <ChevronDownIcon color="gray.400" />}
      </Flex>

      {/* Collapsible body */}
      {isOpen && (
        <Box
          border="1px solid"
          borderTop="none"
          borderColor={hasPhysicalExam ? 'green.300' : 'gray.200'}
          _dark={{ borderColor: hasPhysicalExam ? 'green.600' : 'gray.600', bg: 'gray.900' }}
          borderRadius="0 0 6px 6px"
          px={3}
          py={3}
          bg="white">
          <FormElement
            root
            showName={false}
            field={generalPhysicalExamField}
            name="data.physical"
          />

          <Divider my={3} />

          {/* Cervical Exam sub-form */}
          {hasCervicalExam ? (
            <CervicalExamInput />
          ) : (
            <RoundedButton
              size="xs"
              variant="outline"
              onClick={() => {
                form.change('data.hasCervicalExam', true)
                form.change('data.cervicalExam', {})
              }}>
              + Perform Cervical Exam
            </RoundedButton>
          )}

          <Divider my={3} />

          <RoundedButton size="xs" variant="ghost" colorScheme="red" onClick={handleClear}>
            Clear Physical Exam
          </RoundedButton>
        </Box>
      )}
    </Box>
  )
}

export const PelvicExamInput = () => {
  const form = useForm()
  const {
    input: { value: hasPelvicExam },
  } = useField<boolean | undefined>('data.hasPelvicExam', { subscription: { value: true } })
  const [isOpen, setIsOpen] = useState(!!hasPelvicExam)

  // Lazy import to avoid circular dependency — pelvicExamField is in @hb/shared
  const handleOpen = useCallback(() => {
    form.change('data.hasPelvicExam', true)
    setIsOpen(true)
  }, [form])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      setIsOpen(false)
    } else {
      handleOpen()
    }
  }, [isOpen, handleOpen])

  const handleClear = useCallback(() => {
    form.change('data.hasPelvicExam', false)
    form.change('data.pelvic', undefined)
    setIsOpen(false)
  }, [form])

  return (
    <Box data-child-name="data.pelvic" w="100%" px={4} py={3}>
      <Flex
        align="center"
        justify="space-between"
        px={3}
        py={2}
        borderRadius={isOpen ? '6px 6px 0 0' : 6}
        border="1px solid"
        borderColor={hasPelvicExam ? 'green.300' : 'gray.200'}
        bg={hasPelvicExam ? 'green.50' : 'gray.50'}
        _dark={{
          bg: hasPelvicExam ? 'green.900' : 'gray.800',
          borderColor: hasPelvicExam ? 'green.600' : 'gray.600',
        }}
        cursor="pointer"
        onClick={handleToggle}>
        <Flex align="center" gap={2}>
          <SectionLabel>Pelvic Exam</SectionLabel>
          {hasPelvicExam && (
            <Badge colorScheme="green" fontSize="2xs">
              Documented
            </Badge>
          )}
          {!hasPelvicExam && (
            <Text fontSize="xs" color="gray.400" fontStyle="italic">
              Tap to expand and document
            </Text>
          )}
        </Flex>
        {isOpen ? <ChevronUpIcon color="gray.400" /> : <ChevronDownIcon color="gray.400" />}
      </Flex>

      {isOpen && (
        <Box
          border="1px solid"
          borderTop="none"
          borderColor={hasPelvicExam ? 'green.300' : 'gray.200'}
          _dark={{ borderColor: hasPelvicExam ? 'green.600' : 'gray.600', bg: 'gray.900' }}
          borderRadius="0 0 6px 6px"
          px={3}
          py={3}
          bg="white">
          <VStack spacing={2} align="stretch">
            {(
              ['vulva', 'vaginalPink', 'cervix', 'uterus', 'adnexa', 'rectum', 'pelvis'] as const
            ).map(key => (
              <PelvicFieldInput key={key} fieldKey={key} />
            ))}
          </VStack>
          <Divider my={3} />
          <RoundedButton size="xs" variant="ghost" colorScheme="red" onClick={handleClear}>
            Clear Pelvic Exam
          </RoundedButton>
        </Box>
      )}
    </Box>
  )
}

// Lightweight text input for each pelvic field (mirrors how EXAM_SECTION renders notes)
const PELVIC_LABELS: Record<string, string> = {
  vulva: 'Vulva',
  vaginalPink: 'Vaginal / Rugae',
  cervix: 'Cervix',
  uterus: 'Uterus',
  adnexa: 'Adnexa',
  rectum: 'Rectum',
  pelvis: 'Pelvis',
}

const PelvicFieldInput = ({ fieldKey }: { fieldKey: string }) => {
  const form = useForm()
  const {
    input: { value },
  } = useField<{ notes?: string } | undefined>(`data.pelvic.${fieldKey}`, {
    subscription: { value: true },
  })
  return (
    <Flex align="center" gap={2}>
      <Text fontSize="xs" color="gray.500" w="90px" flexShrink={0}>
        {PELVIC_LABELS[fieldKey]}
      </Text>
      <Box flex={1} minW={0}>
        <input
          style={{
            width: '100%',
            fontSize: '0.8rem',
            padding: '4px 8px',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            outline: 'none',
            background: 'transparent',
          }}
          value={value?.notes ?? ''}
          placeholder="Normal / findings..."
          onChange={e =>
            form.change(`data.pelvic.${fieldKey}`, { notes: e.target.value || undefined })
          }
        />
      </Box>
    </Flex>
  )
}
